import { GridCellParams } from '@mui/x-data-grid-premium';
import { useEffect, useRef, useState } from 'react';
import { type PlaceOption, usePlaceOptions } from 'src/api/placeOptions.api';
import formatAddress from 'src/utils/address/formatAddress';
import { AddressRole } from 'types/graphql';
import AutoCompleteCell from './AutoCompleteCell';

type Props = GridCellParams & { addressRoles?: AddressRole[]; businessEntityId?: number | null; strict?: boolean };

/** The server already matched the typed text (TAS-3660 P7 BZ77); the cell shows its page as-is. */
const serverMatched = (options: PlaceOption[]) => options;

export default function PlaceCell({ hasFocus, addressRoles, businessEntityId, strict, ...params }: Props) {
  const ref = useRef<HTMLInputElement>();
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    if (hasFocus) {
      ref.current?.focus({ preventScroll: true });
    }
  }, [hasFocus]);

  const selectedId = typeof params.value === 'number' ? params.value : null;
  const { places } = usePlaceOptions({ addressRoles, businessEntityId, strict, searchText, selectedId });

  const value = places.find((p) => p.id === params.value);

  return (
    <AutoCompleteCell<PlaceOption>
      textFieldProps={{ inputRef: ref }}
      {...params}
      // This is the one AutoCompleteCell rendered from `renderCell`, on a column
      // that is not editable (`usePeopleColumns.tsx:53`) — it drives its own focus
      // above and never hands the editor the grid's. Entering an EDITABLE cell
      // opens its listbox; arrowing across the People grid must not.
      hasFocus={false}
      value={value}
      isOptionEqualToValue={(option, value) =>
        typeof value === 'object' ? option.id === value.id : option.id === value
      }
      getOptionLabel={(place) => formatAddress(place)}
      options={places}
      filterOptions={serverMatched}
      // Only typing searches: MUI also reports the selected option's label ('reset'), which is not a query.
      onInputChange={(_event, text, reason) => {
        if (reason === 'input') setSearchText(text);
        else if (reason === 'clear') setSearchText('');
      }}
    />
  );
}
